// Settings page — entry point.
//
// Renders the tab strip and delegates the active-tab content area to the
// selected tab module's `mount()`. Tabs are imported here in display order;
// each is a separate module under `./tabs/`.
//
// Active-tab persistence: localStorage key `tkos.settings.active_tab`.
// Re-mounted when the user switches tabs; old tab's cleanup runs first.

import { registerPage } from "../../shell/router";
import { can } from "../../shell/role-gate";
import type { Page, PageContext, PageCleanup } from "../../shell/types";
import type { SettingsTab, TabCleanup } from "./types";
import "./styles.css";

// ── Import all 12 tab modules in display order ──────────────────────────────
import teamPermissions from "./tabs/team-permissions";
import integrations from "./tabs/integrations";
import telephony from "./tabs/telephony";
import calendarTab from "./tabs/calendar";
import branding from "./tabs/branding";
import paymentTerms from "./tabs/payment-terms";
import workingHours from "./tabs/working-hours";
import wizardBranding from "./tabs/wizard-branding";
import wizardAppointments from "./tabs/wizard-appointments";
import companyProfile from "./tabs/company-profile";
import deliveryAddresses from "./tabs/delivery-addresses";
import suppliers from "./tabs/suppliers";
import socialMedia from "./tabs/social-media";
import aiAssistant from "./tabs/ai-assistant";
import websiteIntegration from "./tabs/website-integration";
import payments from "./tabs/payments";
import billing from "./tabs/billing";
import notifications from "./tabs/notifications";
import reviews from "./tabs/reviews";
import messageTemplates from "./tabs/message-templates";
import smsNotifications from "./tabs/sms-notifications";
import competitorPlaybook from "./tabs/competitor-playbook";
import password from "./tabs/password";
import account from "./tabs/account";

const TABS: readonly SettingsTab[] = [
  teamPermissions,
  integrations,
  telephony,
  calendarTab,
  branding,
  paymentTerms,
  workingHours,
  wizardBranding,
  wizardAppointments,
  companyProfile,
  deliveryAddresses,
  suppliers,
  socialMedia,
  aiAssistant,
  websiteIntegration,
  payments,
  billing,
  reviews,
  messageTemplates,
  smsNotifications,
  competitorPlaybook,
  notifications,
  password,
  account,
] as const;

const STORAGE_KEY = "tkos.settings.active_tab";

// ── Per-tab permission gating ───────────────────────────────────────────────
// Each tab maps to a permission key from the 18-perm role matrix; tabs not
// listed require the general "settings" permission. A role that lacks the
// permission sees the tab GREYED with a tooltip (not a 403 on click).
// Owner always passes (can() returns true for owner). While the matrix is still
// loading, can() is permissive so nothing flickers locked.
const TAB_PERM: Record<string, string> = {
  "team-permissions": "team-management",
  payments: "financials",
  billing: "financials",
  suppliers: "suppliers",
  "ai-assistant": "ai-tools",
};
// Everyone may change their own password, whatever their role.
const OPEN_TO_ALL = new Set(["password"]);
function tabAllowed(tab: SettingsTab): boolean {
  if (OPEN_TO_ALL.has(tab.id)) return true;
  return can(TAB_PERM[tab.id] ?? "settings");
}
function lockMessage(label: string): string {
  return `Your role doesn't have access to ${label}. Ask your account owner if you need it.`;
}

function readStoredTabId(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeStoredTabId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore quota/private-mode */
  }
}

function pickInitialTab(requestedTabId?: string): SettingsTab {
  // A `tab` route param (e.g. `#/settings?tab=payments`) wins — lets other
  // flows deep-link straight to a tab (e.g. the Stripe Connect callback
  // returning the retailer to Settings → Payments).
  if (requestedTabId) {
    const requested = TABS.find((t) => t.id === requestedTabId);
    if (requested && tabAllowed(requested)) return requested;
  }
  const stored = readStoredTabId();
  if (stored) {
    const match = TABS.find((t) => t.id === stored);
    if (match && tabAllowed(match)) return match;
  }
  // Never land on a tab the role can't access — pick the first allowed one.
  return TABS.find(tabAllowed) ?? TABS[0]!;
}

const settingsPage: Page = {
  name: "settings",
  label: "Settings",
  // Sidebar icon glyph — the modular chrome (megabar) renders its own
  // SVGs per-page-group, so this Page.icon field isn't surfaced today.
  // Left blank rather than carrying an emoji that no one ever sees.
  icon: "",
  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let activeTabId = pickInitialTab(ctx.params["tab"]).id;
    let activeCleanup: TabCleanup | null = null;

    // Build the shell
    const root = document.createElement("div");
    root.className = "set-root";

    const header = document.createElement("div");
    header.className = "set-header";
    header.innerHTML = `
      <h1>Settings</h1>
      <p>Team, integrations, branding, payments, and how the wizard appears on your site.</p>
    `;
    root.appendChild(header);

    const tabStrip = document.createElement("nav");
    tabStrip.className = "set-tabs";
    tabStrip.setAttribute("role", "tablist");
    tabStrip.setAttribute("aria-label", "Settings sections");

    for (const tab of TABS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "set-tab";
      btn.dataset["tabId"] = tab.id;
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-controls", "set-content");
      btn.setAttribute("aria-selected", String(tab.id === activeTabId));
      // Greyed + tooltip (not a 403) for tabs the user's role can't access. We
      // use aria-disabled + a class rather than the `disabled` attribute so the
      // native title tooltip still fires on hover (disabled buttons swallow
      // mouse events). Activation is blocked in switchTab / keyboard nav.
      if (!tabAllowed(tab)) {
        btn.classList.add("set-tab--locked");
        btn.setAttribute("aria-disabled", "true");
        btn.title = lockMessage(tab.label);
      }
      btn.innerHTML = `
        ${tab.icon ? `<span class="set-tab-icon" aria-hidden="true">${tab.icon}</span>` : ""}
        <span>${tab.label}</span>
      `;
      tabStrip.appendChild(btn);
    }
    root.appendChild(tabStrip);

    const content = document.createElement("div");
    content.className = "set-content";
    content.id = "set-content";
    content.setAttribute("role", "tabpanel");
    root.appendChild(content);

    container.replaceChildren(root);

    function switchTab(id: string): void {
      const next = TABS.find((t) => t.id === id);
      if (!next) return;
      if (!tabAllowed(next)) return; // locked tab — ignore activation
      if (next.id === activeTabId && activeCleanup) {
        // Re-click on active tab is a no-op (don't re-mount needlessly).
        return;
      }

      // Tear down the previous tab.
      if (activeCleanup) {
        try {
          activeCleanup();
        } catch (err) {
          console.warn(`[settings] cleanup for "${activeTabId}" threw:`, err);
        }
        activeCleanup = null;
      }

      // Update tab-strip aria state.
      for (const btn of Array.from(tabStrip.querySelectorAll<HTMLButtonElement>(".set-tab"))) {
        btn.setAttribute("aria-selected", String(btn.dataset["tabId"] === id));
      }

      // Mount the new tab.
      content.replaceChildren();
      content.setAttribute("aria-labelledby", `set-tab-${id}`);

      try {
        activeCleanup = next.mount(content, ctx);
      } catch (err) {
        console.error(`[settings] mount of "${id}" threw:`, err);
        content.innerHTML = `<div class="set-empty">Tab "${next.label}" failed to load. See console.</div>`;
        activeCleanup = null;
      }

      activeTabId = id;
      writeStoredTabId(id);
    }

    // Wire tab clicks (event delegation).
    const onTabClick = (e: Event): void => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>(".set-tab");
      if (!btn) return;
      const id = btn.dataset["tabId"];
      if (!id) return;
      switchTab(id);
    };
    tabStrip.addEventListener("click", onTabClick);

    // Keyboard nav across tab strip (ArrowLeft/Right/Home/End).
    const onTabKey = (e: KeyboardEvent): void => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
      // Keyboard nav skips locked tabs (only walk the accessible ones).
      const buttons = Array.from(tabStrip.querySelectorAll<HTMLButtonElement>(".set-tab:not(.set-tab--locked)"));
      const currentIdx = buttons.findIndex((b) => b.dataset["tabId"] === activeTabId);
      if (currentIdx < 0) return;
      let nextIdx = currentIdx;
      if (e.key === "ArrowLeft") nextIdx = (currentIdx - 1 + buttons.length) % buttons.length;
      else if (e.key === "ArrowRight") nextIdx = (currentIdx + 1) % buttons.length;
      else if (e.key === "Home") nextIdx = 0;
      else if (e.key === "End") nextIdx = buttons.length - 1;
      const nextBtn = buttons[nextIdx];
      if (!nextBtn) return;
      e.preventDefault();
      nextBtn.focus();
      const nextId = nextBtn.dataset["tabId"];
      if (nextId) switchTab(nextId);
    };
    tabStrip.addEventListener("keydown", onTabKey);

    // Initial mount.
    switchTab(activeTabId);

    // Cleanup callback for the router.
    return () => {
      tabStrip.removeEventListener("click", onTabClick);
      tabStrip.removeEventListener("keydown", onTabKey);
      if (activeCleanup) {
        try {
          activeCleanup();
        } catch {
          /* ignore */
        }
        activeCleanup = null;
      }
    };
  },
};

registerPage(settingsPage);
